import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Alert, Appearance, Platform, ViewStyle, ImageStyle } from "react-native"
import { Button, Screen, AutoFeederPondForm as ModelForm, Text, AutoImage, useAutoFeederPond } from "../../../../components"
import { useStores } from "../../../../models"
import { color, nativeBaseTheme } from "../../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { View, Modal, useDisclose } from "native-base"
import SyncableModel from "../../../../database/SyncableModel"
import { goBack, navigate } from "../../../../navigators"
import { FormikProps, FormikValues } from "formik"
import { AutoFeederPond } from "../../../../database"
import _ from "lodash"
import { SafeAreaView } from "react-native-safe-area-context"
import { translate } from "../../../../i18n"
import moment from "moment"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flexGrow: 1,
}

const SPACE: ViewStyle = {
  flex: 1,
}

const BUTTON_CONTAINER: ViewStyle = {
  borderTopWidth: 1,
  borderTopColor: color.palette.gray300,
}

const DELETE_BUTTON: ViewStyle = {
  alignSelf: "flex-end",
  marginVertical: 8,
  marginHorizontal: 16
}

const BUTTON: ViewStyle = {
  marginVertical: 8,
  marginHorizontal: 16
}

const MODAL: ViewStyle = {
  margin: 0,
  height: '100%',
  width: '100%',
  justifyContent: 'center',
  flexGrow: 1,
  backgroundColor: color.palette.white,
  paddingHorizontal: 16,
  paddingBottom: 20,
  alignItems: 'center'
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  backgroundColor: color.primary,
  alignItems: 'center',
  width: '60%',
  borderRadius: 5,
}

const UNRECOGNIZED_IMAGE: ImageStyle = {
  // width: '100%',
  height: 170,
  resizeMode: 'contain',
}

const rojali = require("./rojali.png")

const SuccessModal: FC<{ colorScheme?: "light" | "dark", isOpen?: boolean, onClose?: () => void }> = observer(function SuccessModal(props) {
  const { colorScheme, isOpen, onClose } = props
  const { userStore: { user } } = useStores();
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <SafeAreaView />
      <View
        style={MODAL}
        borderTopRadius="10px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <AutoImage source={rojali} style={UNRECOGNIZED_IMAGE} />
        <Text
          text={`Hi ${user.name} 👋`}
          preset="title1"
          colorScheme={colorScheme}
          color={color.palette.gray800}
          marginBottom="8px"
          textAlign="center"
        />
        <Text
          text={translate("autoFeederPondCreateScreen.successAddSubtitle")}
          transformText={_.upperFirst}
          preset="body2"
          colorScheme={colorScheme}
          color={color.palette.gray800}
          marginBottom="25px"
          textAlign="center"
        />
        <Text
          text={translate("autoFeederPondCreateScreen.successModalSubtitle")}
          transformText={_.upperFirst}
          preset="body2"
          colorScheme={colorScheme}
          color={color.palette.gray800}
          marginBottom="50px"
          textAlign="center"
        />
        <Button
          testID="jala:id/next-button-modal"
          text={translate("common.next")}
          colorScheme={colorScheme}
          preset="primary"
          textColor={color.palette.white}
          style={BUTTON_DETAIL_ITEM}
          onPress={onClose}
        />
      </View>
    </Modal>
  )
})

export const AutoFeederPondCreateScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function AutoFeederPondCreateScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params } } = props
  const { setAutoFeederPond } = useAutoFeederPond()

  // const [ isError, setIsError ] = useState<boolean>(false)
  const [ model, setModel ] = useState<SyncableModel>(null)
  // Pull in one of our MST stores
  const {
    appStore: { farmId, farmStore },
    autoFeederPondStore: modelStore,
    autoFeederPondStore: { loading, error: modelError }
  } = useStores()
  const { isOpen, onClose, onOpen } = useDisclose()

  const modelForm = useRef<FormikProps<FormikValues>>()
  const modelFormRef = useCallback((node) => {
    modelForm.current = node;
    // if (modelForm.current) setIsError(!(modelForm.current.dirty && modelForm.current.isValid))
  }, [])

  const initialValues = useMemo<any>(() => {
    return { farm_id: farmId, auto_feeder_address: params?.device?.id, name: params?.device?.name, ...(model ? model.toArray() : params) }
  }, [farmId, model, params])

  useEffect(() => {
    const autoFeederPond = farmStore.autoFeederPonds.find((m) => m.id === params?.id)
    if (autoFeederPond) {
      const model = new AutoFeederPond();
      model.fill(autoFeederPond)
      model.syncOriginal()
      model.exists = true
      setModel(model)
    }
  }, [farmStore, params])

  useEffect(() => {
    modelStore.init()
  }, [modelStore])

  useEffect(() => {
    if (!modelStore) return;
    if (modelError) {
      if (modelError.response && modelError.response.status === 422) {
        const errors = {}
        Object.keys(modelError.response.data.errors).forEach((key) => {
          errors[key] = (modelError.response.data.errors[key] || [])[0]
        })
        if (modelForm.current) modelForm.current.setErrors(errors)
        modelStore.set('error', null)
      }
      else {
        if (Platform.OS === 'web') {
          if (modelError.response)
            confirm(modelError.response.originalError.message)
          modelStore.set('error', null)
        }
        else
          Alert.alert(
            'Error',
            modelError.message,
            [ { onPress: () => modelStore.set('error', null) } ]
          );
      }
    }
  }, [modelError, modelStore])

  const onSubmit = useCallback(async (data) => {
    modelStore.set('loading', true)
    const success = !model ?
      await modelStore.store({
        ...data,
        state: {
          mode: 'continuous',
          debit: 350,
          startTime: '00:00:00',
          endTime: '23:59:59',
          onTime: 1,
        }
      }, (success, callbackModel) => {
        farmStore.set('pondId', data.pond_id)
        farmStore.pondStore.set('autoFeederPondId', callbackModel.id)
        setAutoFeederPond(callbackModel)
      }) :
      await modelStore.update(model, {
        ...data,
        state: {
          ...data.state,
          updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        }
      })
    if (success) {
      await modelStore.set('needRefresh', true)
      onOpen()
    }
    modelStore.set('loading', false)
  }, [farmStore, model, modelStore, onOpen, setAutoFeederPond])

  const handleSubmit = useCallback((event) => {
    if (modelForm.current) modelForm.current.handleSubmit(event)
  }, [])

  const handleDelete = useCallback(async () => {
    modelStore.set('loading', true)
    const success = await modelStore.delete(model)
    if (success) {
      modelStore.set('needRefresh', true)
      goBack('Home.Stack')
    }
    modelStore.set('loading', false)
  }, [model, modelStore])

  const onNavigateToWizard = useCallback(() => {
    JalaAnalytic.log('Mobile App : Register Asto');
    navigate("Home.Stack", "Home.AutoFeederPond.Wizard.Connection.Setup")
    onClose();
  }, [onClose])

  const SuccessModalComponent = useCallback(() => {
    return (
      <SuccessModal isOpen={isOpen} onClose={onNavigateToWizard} />
    )
  }, [isOpen, onNavigateToWizard])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === 'light' ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      <ModelForm
        colorScheme={colorScheme}
        initialValues={initialValues}
        params={params}
        innerRef={modelFormRef}
        onSubmit={onSubmit}
      />
      <View style={SPACE} />
      <View style={BUTTON_CONTAINER}>
        {model && <Button
          testID="jala:id/delete-button"
          tx="delete"
          preset="link"
          colorScheme={colorScheme}
          textColor={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
          disabled={loading}
          onPress={handleDelete}
          style={DELETE_BUTTON}
        />}
        <Button
          testID="jala:id/save-button"
          tx="save"
          colorScheme={colorScheme}
          // disabled={isError}
          loading={loading}
          onPress={handleSubmit}
          style={BUTTON}
        />
      </View>
      <SuccessModalComponent />
    </Screen>
  )
})
